import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DownloadIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	FormControl,
	HStack,
	Icon,
	Input,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Select,
	SimpleGrid,
	Td,
	Text,
	Textarea,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment/moment";
import { FiFileText, FiHash, FiInfo, FiMapPin, FiShield, FiUpload, FiUser } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyConfirmModal,
	PropertyModalSection,
	PropertyModalShell,
} from "../PropertyModal/shared";
import { removeSecurityDepositAsync, updateSecurityDepositAsync } from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { API } from "../../utils/http";
import { addSecurityDepositVerifier } from "../../utils/verifier";
import Dropzone from "../Dropzone/Dropzone";

const locationOptions = ["custodyBank", "custodyCash", "returned", "returnedTenant", "owner"];

export default function SecurityDepositRow(props) {
	const { addedDate, updatedDate, amount, location, custodian, description, id, currency, propertyId, documents } = props;
	const { isOpen: isMarkAsPaidOpen, onOpen: onMarkAsPaidOpen, onClose: onMarkAsPaidClose } = useDisclosure();
	const { isOpen: isDeleteConfirmationOpen, onOpen: onDeleteConfirmationOpen, onClose: onDeleteConfirmationClose } = useDisclosure();
	const [error, setError] = useState({});
	const toast = useToast();
	const dispatch = useDispatch();
	const initialMarkAsPaidInformation = {
		location: location,
		custodian: custodian,
		description: description,
		propertyId: propertyId,
		securityDepositId: id,
		documents: [],
	};
	const { t } = useTranslation();
	const [markAsPaidInformation, setMarkAsPaidInformation] = useState(initialMarkAsPaidInformation);

	const iconColor = useColorModeValue("blue.500", "blue.300");

	const handleChangeMarkAsPaidInformation = (e) => {
		setMarkAsPaidInformation({ ...markAsPaidInformation, [e.target.name]: e.target.value });
	};
	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		let documents = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			documents.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}

		setMarkAsPaidInformation({ ...markAsPaidInformation, documents });
	};

	const handleSaveMarkAsPaid = () => {
		console.log(markAsPaidInformation);
		const errors = addSecurityDepositVerifier(markAsPaidInformation);
		console.log(errors);
		setError(errors);
		if (Object.keys(errors).length > 0) {
			toast({
				title: "Error encountered",
				description: "Please fill in all the required fields",
				status: "error",
				duration: 9000,
				isClosable: true,
			});
			return;
		}
		dispatch(updateSecurityDepositAsync(markAsPaidInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Security Deposit updated!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onMarkAsPaidClose();
			});
	};

	const handleGetDocument = (documentId, documentName) => {
		const payload = { propertyId, documentId, securityDepositId: id, documentName };
		console.log(payload);
		API.put("get-security-deposit-document", payload)
			.then((r) => {
				console.log(r);
				window.open(r.data.url, "_blank");
			})
			.catch((e) => {
				console.log(e);
				toast({
					title: "Error encountered",
					description: e.response.data.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};
	const handleDeletePayment = () => {
		const payload = { propertyId, securityDepositId: id };
		console.log(payload);
		onDeleteConfirmationClose();
		dispatch(removeSecurityDepositAsync(payload));
	};
	const getLocationColorScheme = (location) => {
		if (location === "custodyBank") return "yellow";
		if (location === "custodyCash") return "green";
		if (location === "returned") return "red";
		if (location === "owner") return "blue";
	};

	const renderMarkAsPaidModal = () => (
		<PropertyModalShell
			isOpen={isMarkAsPaidOpen}
			onClose={onMarkAsPaidClose}
			size="4xl"
			icon={FiShield}
			title={t("property.securityDepositDetails")}
			subtitle={t("property.updateSecurityDepositSubtitle")}
			footer={<ModalFooterActions onCancel={onMarkAsPaidClose} onConfirm={handleSaveMarkAsPaid} confirmLabel={t("misc.save")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.depositSummary")}>
						<VStack spacing={3} align="stretch">
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiHash} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.amount")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="bold">
									{amount} {currency}
								</Text>
							</HStack>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiMapPin} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("property.currentLocation")}:
									</Text>
								</HStack>
								<Badge colorScheme={getLocationColorScheme(location)} fontSize="sm" px={3} py={1} borderRadius="full">
									{t(`types.${location}`)}
								</Badge>
							</HStack>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiUser} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.custodian")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="medium">
									{custodian || t("property.notSpecified")}
								</Text>
							</HStack>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiShield} title={t("property.updateInformation")}>
						<VStack spacing={3} align="stretch">
							<FormControl>
								<ModalFieldLabel icon={FiUser}>{t("property.custodianLabel")}</ModalFieldLabel>
								<Input
									size="sm"
									isInvalid={error?.custodian}
									type="text"
									name="custodian"
									placeholder={t("property.custodianPlaceholder")}
									borderRadius="md"
									value={markAsPaidInformation.custodian}
									onChange={handleChangeMarkAsPaidInformation}
								/>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiMapPin}>{t("property.locationLabel")}</ModalFieldLabel>
								<Select size="sm" onChange={handleChangeMarkAsPaidInformation} name="location" borderRadius="md" value={markAsPaidInformation.location}>
									{locationOptions.map((option) => (
										<option key={option} value={option}>
											{t(`types.${option}`)}
										</option>
									))}
								</Select>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.descriptionLabel")}</ModalFieldLabel>
								<Textarea
									size="sm"
									isInvalid={error?.description}
									name="description"
									placeholder={t("property.descriptionPlaceholder")}
									borderRadius="md"
									value={markAsPaidInformation.description}
									onChange={handleChangeMarkAsPaidInformation}
									rows={3}
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiFileText}
						title={t("property.existingDocuments")}
						headerExtra={
							<Badge colorScheme="blue" fontSize="xs">
								{documents?.length || 0}
							</Badge>
						}
					>
						{documents && documents.length > 0 ? (
							<VStack spacing={3} align="stretch">
								{documents.map((d, index) => (
									<Box
										key={index}
										p={3}
										border="1px"
										borderColor="gray.200"
										borderRadius="md"
										_hover={{ bg: "gray.50" }}
										cursor="pointer"
										onClick={() => handleGetDocument(d.id, d.documentName)}
									>
										<HStack justify="space-between">
											<HStack spacing={3}>
												<Icon as={FiFileText} color={iconColor} boxSize={4} />
												<VStack align="start" spacing={0}>
													<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
														{d.documentName}
													</Text>
													<Text fontSize="xs" color="gray.500">
														{t("property.clickToDownload")}
													</Text>
												</VStack>
											</HStack>
											<Icon as={DownloadIcon} color={iconColor} boxSize={4} />
										</HStack>
									</Box>
								))}
							</VStack>
						) : (
							<Box textAlign="center" py={6}>
								<Icon as={FiFileText} color="gray.300" boxSize={8} mb={3} />
								<Text fontSize="sm" color="gray.500">
									{t("property.noDocumentsAttached")}
								</Text>
							</Box>
						)}
					</PropertyModalSection>

					<PropertyModalSection icon={FiUpload} title={t("property.addNewDocument")}>
						<Dropzone
							onDrop={onDocumentDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
							}}
							description={t("property.uploadPdfDocDocx")}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	const renderDeleteConfirmationModal = () => (
		<PropertyConfirmModal
			isOpen={isDeleteConfirmationOpen}
			onClose={onDeleteConfirmationClose}
			onConfirm={handleDeletePayment}
			message={t("property.deleteSecurityDepositConfirmation")}
			emphasis={t("property.actionCannotBeUndone")}
		/>
	);

	return (
		<Tr>
			<Td>
				<Text fontSize="md" minWidth="100%">
					{moment(addedDate).format("DD MMM YYYY - HH:mm")}
				</Text>
			</Td>
			<Td>
				<Badge fontSize="md" colorScheme={getLocationColorScheme(location)} textTransform={"none"}>
					{t(`types.${location}`)}
				</Badge>
			</Td>
			<Td>
				<Text fontSize="md">
					{amount} {currency}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md">{custodian}</Text>
			</Td>
			<Td>
				<Text fontSize="md">{description}</Text>
			</Td>

			<Td>
				<Text fontSize="md">{moment(updatedDate).format("DD MMM YYYY - HH:mm")}</Text>
			</Td>
			<Td>
				<Menu closeOnSelect={false}>
					<MenuButton>
						<Icon as={HamburgerIcon} />
					</MenuButton>
					<MenuList>
						<MenuItem onClick={onMarkAsPaidOpen}>Change Status</MenuItem>
						<MenuItem onClick={onDeleteConfirmationOpen}>Delete</MenuItem>
					</MenuList>
				</Menu>
				{renderMarkAsPaidModal()}
				{renderDeleteConfirmationModal()}
			</Td>
		</Tr>
	);
}
